import { KILO } from 'constants/metricPrefixes'
import { TerminalItem, TerminalLimitsItem } from 'src/schema'
import { div } from 'src/shared/helpers/number'

/**
 * Коды лимитов терминалов
 */
export const TERMINAL_LIMIT_CODES = {
  MAX_WEIGHT: 'maxWeight',
  MAX_HEIGHT: 'maxHeight',
  MAX_WIDTH: 'maxWidth',
  MAX_LENGTH: 'maxLength',
  HAS_LOADER: 'hasLoader',
  HAS_RAMP: 'hasRamp'
} as const

/**
 * Значения по умолчанию для лимитов терминалов (в граммах для веса, в см для размеров)
 * Используются, если у терминала не задан лимит
 */
export const DEFAULT_TERMINAL_LIMITS = {
  /** Максимальный вес в граммах (по умолчанию 20 000 кг = 20 000 000 г) */
  [TERMINAL_LIMIT_CODES.MAX_WEIGHT]: 20_000_000,
  /** Максимальная высота в см (по умолчанию 210 см) */
  [TERMINAL_LIMIT_CODES.MAX_HEIGHT]: 210,
  /** Максимальная ширина в см (по умолчанию 210 см) */
  [TERMINAL_LIMIT_CODES.MAX_WIDTH]: 210,
  /** Максимальная длина в см (по умолчанию 1300 см = 13 м) */
  [TERMINAL_LIMIT_CODES.MAX_LENGTH]: 1300
} as const

/**
 * Получает значение лимита из массива лимитов терминала
 */
export const getTerminalLimitValue = (limits: TerminalLimitsItem[] | undefined, code: string): number | undefined => {
  if (!limits || limits.length === 0) {
    return undefined
  }

  const limit = limits.find((item) => item.code === code)
  return limit?.value
}

/**
 * Получает значение лимита терминала с учетом значения по умолчанию
 */
export const getTerminalLimit = (
  terminal: TerminalItem | null | undefined,
  code: keyof typeof DEFAULT_TERMINAL_LIMITS
): number => {
  const value = getTerminalLimitValue(terminal?.specification?.limits, code)
  return value ?? DEFAULT_TERMINAL_LIMITS[code]
}

/**
 * Получает эффективный лимит маршрута (минимум из двух терминалов)
 * Лимиты терминалов имеют приоритет над всеми остальными ограничениями
 */
export const getRouteLimitValue = (
  departureTerminal: TerminalItem | null | undefined,
  destinationTerminal: TerminalItem | null | undefined,
  code: keyof typeof DEFAULT_TERMINAL_LIMITS
): number => {
  const departureLimit = getTerminalLimit(departureTerminal, code)
  const destinationLimit = getTerminalLimit(destinationTerminal, code)

  return Math.min(departureLimit, destinationLimit)
}

/**
 * Интерфейс эффективных лимитов маршрута
 */
export interface RouteLimits {
  /** Максимальный вес в граммах */
  maxWeightGrams: number
  /** Максимальный вес в кг */
  maxWeightKg: number
  /** Максимальная высота в см */
  maxHeightCm: number
  /** Максимальная высота в м */
  maxHeightM: number
  /** Максимальная ширина в см */
  maxWidthCm: number
  /** Максимальная ширина в м */
  maxWidthM: number
  /** Максимальная длина в см */
  maxLengthCm: number
  /** Максимальная длина в м */
  maxLengthM: number
}

/**
 * Получает все эффективные лимиты маршрута
 */
export const getRouteLimits = (
  departureTerminal: TerminalItem | null | undefined,
  destinationTerminal: TerminalItem | null | undefined
): RouteLimits => {
  const maxWeightGrams = getRouteLimitValue(departureTerminal, destinationTerminal, TERMINAL_LIMIT_CODES.MAX_WEIGHT)
  const maxHeightCm = getRouteLimitValue(departureTerminal, destinationTerminal, TERMINAL_LIMIT_CODES.MAX_HEIGHT)
  const maxWidthCm = getRouteLimitValue(departureTerminal, destinationTerminal, TERMINAL_LIMIT_CODES.MAX_WIDTH)
  const maxLengthCm = getRouteLimitValue(departureTerminal, destinationTerminal, TERMINAL_LIMIT_CODES.MAX_LENGTH)

  return {
    maxWeightGrams,
    maxWeightKg: Number(div(maxWeightGrams, KILO, 2)),
    maxHeightCm,
    maxHeightM: Number(div(maxHeightCm, 100, 2)),
    maxWidthCm,
    maxWidthM: Number(div(maxWidthCm, 100, 2)),
    maxLengthCm,
    maxLengthM: Number(div(maxLengthCm, 100, 2))
  }
}

/**
 * Применяет лимиты терминалов к существующим ограничениям валидации
 * Лимиты терминалов имеют приоритет - берется минимум из лимита терминала и существующего ограничения
 */
export const applyTerminalLimitsToValidation = (currentMax: number, terminalLimit: number): number => {
  return Math.min(currentMax, terminalLimit)
}
